import debounce from 'lodash/debounce';
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';

import { getLinkAriaLabel, getUrlText } from '@/utils/urlUtils';

import {
  handleNavItemSubmenuTriggerKeys,
  navToFindStores,
} from '@/components/Megamenu/helpers';
import type {
  ContentfulMainHeaderFragment,
  ContentfulMainMenuItemFragment,
} from '@/graphql/contentful/api';
import { useRouter } from '@/hooks/useRouter';
import { useConfigProvider } from '@/providers/ConfigProvider';
import { LocaleContext } from '@/providers/LocaleProvider';
import { useRegionProvider } from '@/providers/RegionProvider';
import { searchStateActions, useSearchState } from '@/states/searchState';
import type { HeaderThemeKeys } from '@/theme/types/components';

import { Submenu } from './components';
import { Collections } from './components/Collections';

import * as S from './Megamenu.styles';

export interface MegamenuProps {
  headerBackground: HeaderThemeKeys;
  hideDecoration?: boolean;
  isDisplayed?: boolean;
  isShrunk?: boolean;
  navItems: Partial<ContentfulMainHeaderFragment>;
  textColorOption?: string;
}

export const Megamenu = memo(
  ({
    headerBackground,
    hideDecoration = false,
    isDisplayed = true,
    isShrunk = false,
    navItems,
    textColorOption = 'dark',
  }: MegamenuProps) => {
    const { overlayStatus } = useSearchState();
    const { headerRef } = useConfigProvider();
    const locale = useContext(LocaleContext);
    const { asPath } = useRouter();

    const navRef = useRef<HTMLElement | null>(null);
    const {
      country: { code },
      locale: regionLocale,
    } = useRegionProvider();

    const [top, setTop] = useState(0);
    const [activeSubmenu, setActiveSubmenu] = useState<string>('');
    const [activeMenu, setActiveMenu] = useState<string>('');

    let keyNumber = 0;
    useEffect(() => {
      if (!activeSubmenu) {
        navItems?.megamenu?.menuItemsCollection?.items.some((item) => {
          if (item?.url) {
            const urlRegex = new RegExp(item.url);
            if (urlRegex.test(asPath)) {
              setActiveMenu(item?.internalName ?? '');
              return true;
            }
          }
          setActiveMenu('');
          return false;
        });
      } else if (asPath?.includes(locale)) {
        setActiveMenu('');
      }
    }, [asPath, activeSubmenu, locale]);

    useEffect(() => {
      if (headerRef?.current) {
        setTop(headerRef.current?.offsetHeight ?? 0);
      }
      const handleMouseLeave = (e: MouseEvent) => {
        if (navRef.current && !navRef.current.contains(e.target as Node)) {
          setActiveSubmenu('');
        }
      };

      document.addEventListener('mouseleave', handleMouseLeave, true);

      return () =>
        document.removeEventListener('mouseleave', handleMouseLeave, true);
    }, [headerRef?.current?.clientHeight, isDisplayed]);

    useEffect(() => {
      if (!isDisplayed) {
        setTop(0);
        setActiveSubmenu('');
      }
    }, [isDisplayed]);

    const handleMouseOver = debounce(
      (navItem: ContentfulMainMenuItemFragment | null) => {
        setTop(headerRef?.current?.offsetHeight ?? 0);
        setActiveSubmenu(navItem?.internalName ?? '');
        setActiveMenu('');
      },
      160,
    );

    const handleMouseOut = () => {
      handleMouseOver.cancel();
    };

    const closeSubmenu = useCallback(() => {
      handleMouseOver.cancel();
      setActiveSubmenu('');
      setActiveMenu('');
    }, [handleMouseOver]);

    if (!navItems?.megamenu?.menuItemsCollection?.items?.length) return null;

    return (
      <S.Nav
        ref={navRef}
        $headerBackground={headerBackground}
        $isShrunk={isShrunk}
        $searchOverlay={overlayStatus as string}
        $textColorOption={textColorOption}
        className='megaMenu'
        data-testid='MegamenuComponent'
        onClick={() => searchStateActions.changeOverlayStatus('CLOSE')}
      >
        <ul
          onKeyDownCapture={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              e.stopPropagation();
              closeSubmenu();
            } else if (e.key === 'Tab') {
              closeSubmenu();
            }
          }}
          role='menubar'
          style={{ overflow: 'visible' }}
        >
          {navItems?.megamenu?.menuItemsCollection?.items.map(
            (navItem, index) =>
              navItem?.visibleInDesktop && (
                <li key={'menu' + keyNumber++} role='none'>
                  <S.Item
                    $headerBackground={headerBackground}
                    $hideDecoration={hideDecoration}
                    $isHover={
                      activeSubmenu === navItem?.internalName ||
                      activeMenu === navItem?.internalName
                    }
                    aria-expanded={
                      activeSubmenu === navItem?.internalName ||
                      activeMenu === navItem?.internalName
                    }
                    aria-haspopup={navItem?.type !== 'Link'}
                    data-testid={`MegamenuItem-${index}`}
                    id={`MegamenuItem-${index}`}
                    onKeyDown={(e) =>
                      handleNavItemSubmenuTriggerKeys(e, {
                        closeSubmenu,
                        index,
                        isLink: navItem?.type === 'Link',
                        isSubmenuOpen:
                          activeSubmenu === navItem?.internalName ||
                          activeMenu === navItem?.internalName,
                        openSubmenu: () => handleMouseOver(navItem),
                      })
                    }
                    onMouseOut={() => handleMouseOut()}
                    onMouseOver={() => handleMouseOver(navItem)}
                    role='menuitem'
                    tabIndex={index === 0 ? 0 : -1}
                  >
                    {navItem?.url ? (
                      <S.MainMenuLink
                        aria-label={getLinkAriaLabel(
                          getUrlText(navItem.url),
                          regionLocale.code,
                        )}
                        as={
                          getUrlText(navItem.url) === '/stores'
                            ? 'a'
                            : undefined
                        }
                        className='mainMenuItemLink'
                        href={
                          getUrlText(navItem.url) === '/stores'
                            ? navToFindStores(locale, code)
                            : getUrlText(navItem.url)
                        }
                        tabIndex={-1}
                        target={navItem?.openUrlInNewTab ? '_blank' : '_self'}
                      >
                        <FormattedMessage
                          defaultMessage={navItem?.label ?? ''}
                          id={'menu' + keyNumber}
                        />
                      </S.MainMenuLink>
                    ) : (
                      <S.Title
                        className='mainMenuItemLink'
                        data-testid={`MegamenuTitle-${index}`}
                      >
                        <FormattedMessage
                          defaultMessage={navItem?.label ?? ''}
                          id={'menu' + keyNumber}
                        />
                      </S.Title>
                    )}
                  </S.Item>
                  {navItem?.type !== 'Link' && (
                    <S.Submenu
                      className='submenuWrapper'
                      data-testid={`MegamenuSubmenu-${index}`}
                      id={'submenuWrapper' + keyNumber}
                      top={top}
                    >
                      <S.SubmenuContainer
                        $isActive={navItem?.internalName === activeSubmenu}
                      >
                        {navItem?.type === 'Collection' ? (
                          <Collections
                            collections={
                              navItem as ContentfulMainMenuItemFragment
                            }
                            setActiveSubmenu={(isActive: string) =>
                              setActiveSubmenu(isActive)
                            }
                          />
                        ) : (
                          <Submenu
                            setActiveSubmenu={(isActive: string) =>
                              setActiveSubmenu(isActive)
                            }
                            subMenu={navItem as ContentfulMainMenuItemFragment}
                          />
                        )}
                      </S.SubmenuContainer>
                    </S.Submenu>
                  )}
                </li>
              ),
          )}
        </ul>
      </S.Nav>
    );
  },
);

Megamenu.displayName = 'Megamenu';
